'use client';

import { SignIn } from '@clerk/nextjs';
import { Globe, Check, ArrowRight, ArrowLeft, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useI18n, locales } from '@/lib/i18n';
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import '@/styles/login.css';
import { StudioshotLogo } from '@/components/icons/StudioshotLogo';
const testimonials = [{
  quote: 'This was fun, easy, and SUPER efficient. They gave clear instructions and made each step easy to follow. The results are professional and way easier and less expensive (not to mention FAR less time consuming) than an actual photoshoot. THANK YOU Studioshot!',
  name: 'Kimberley',
  country: 'United States',
  rating: 5
}, {
  quote: "I was VERY hesitant to have AI generated headshots due to most AI altering my face. This app did an amazing job. I can't believe it wasn't me at a photo shoot.",
  name: 'Kris',
  country: 'United States',
  rating: 5
}, {
  quote: 'I’m surprised by the quality of the photos. They all look very realistic and there are many different options, so there’s plenty to choose from.',
  name: 'Daphne',
  country: 'Netherlands',
  rating: 4
}, {
  quote: "This is my 3rd time using Studioshot for my work headshots and it's AMAZINGGGG. I recommend this site to everyone I know for professional shots. The process is so seamless and quick and the photos are more accurate than any other AI app I tried.",
  name: 'Tara',
  country: 'United States',
  rating: 5
}, {
  quote: "Studioshot was rated as the best AI photography app on Google and they did not disappoint. I'm very happy with the images I now have to use for socials, work, etc.",
  name: 'Joe',
  country: 'United States',
  rating: 5
}, {
  quote: 'Studioshot exceeded my expectations - fast, easy, and incredibly professional results. The images look like a real studio shoot. Highly recommended!',
  name: 'Irina',
  country: 'Argentina',
  rating: 5
}];
function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
  }, []);
  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  // Auto-rotate every 10 seconds
  useEffect(() => {
    const interval = setInterval(goToNext, 10000);
    return () => clearInterval(interval);
  }, [goToNext]);
  const current = testimonials[currentIndex];
  return <div className="absolute bottom-6 left-6 right-6 z-20 flex items-stretch gap-2" data-sentry-component="TestimonialCarousel" data-sentry-source-file="LoginPage.tsx">
      {/* Testimonial Card */}
      <div className="bg-black/60 backdrop-blur-sm rounded-2xl p-5 flex-1">
        <p className="text-white text-base leading-relaxed mb-4">
          {current.quote}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-white/80 text-sm">
            {current.name}, {current.country}
          </span>
          <div className="flex items-center gap-0.5">
            {[...Array(current.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-white text-white" />)}
          </div>
        </div>
      </div>

      {/* Navigation Buttons - Full height, each 50% */}
      <div className="flex flex-col gap-2 self-stretch">
        <button onClick={goToNext} className="flex-1 w-12 bg-black/60 backdrop-blur-sm rounded-lg flex items-center justify-center text-white hover:bg-black/80 transition-colors" aria-label="Next testimonial">
          <ArrowRight className="w-5 h-5" data-sentry-element="ArrowRight" data-sentry-source-file="LoginPage.tsx" />
        </button>
        <button onClick={goToPrev} className="flex-1 w-12 bg-black/60 backdrop-blur-sm rounded-lg flex items-center justify-center text-white hover:bg-black/80 transition-colors" aria-label="Previous testimonial">
          <ArrowLeft className="w-5 h-5" data-sentry-element="ArrowLeft" data-sentry-source-file="LoginPage.tsx" />
        </button>
      </div>
    </div>;
}
export function LoginPage() {
  const {
    t,
    locale,
    setLocale
  } = useI18n();
  const searchParams = useSearchParams();
  const redirectUrl = searchParams.get('redirect_url') || '/overview';

  // Use welcome page as the signup redirect - it will handle the logic
  const signUpRedirectUrl = '/welcome';
  return <div className="min-h-screen w-full font-sans" data-sentry-component="LoginPage" data-sentry-source-file="LoginPage.tsx">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        <div className="hidden lg:flex relative items-center justify-center overflow-hidden">
          {/* Background video with overlay */}
          <div className="absolute inset-0 pointer-events-none">
            <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover object-top" src="/login.mp4" />
            <div className="absolute inset-0 bg-black/20" />
          </div>

          {/* Centered text content */}
          {/* <div className="relative z-10 flex flex-col items-center justify-center text-center text-white px-14 max-w-[720px]">
            <h1 className="text-5xl font-bold leading-[1.2] tracking-[-0.96px] mb-5 max-w-[500px]">
              {t('login.hero_title')}
            </h1>
            <p className="text-lg leading-[1.6] max-w-[500px]">
              {t('login.hero_subtitle')}
            </p>
           </div> */}

          {/* Testimonial Carousel */}
          <TestimonialCarousel data-sentry-element="TestimonialCarousel" data-sentry-source-file="LoginPage.tsx" />
        </div>

        <div className="flex flex-col items-center justify-center p-6 sm:p-8 bg-white lg:max-h-screen lg:overflow-y-auto relative">
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
            <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-source-file="LoginPage.tsx">
              <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="LoginPage.tsx">
                <Button variant="ghost" size="icon" className="text-primary hover:bg-accent dark:hover:bg-slate-800" data-sentry-element="Button" data-sentry-source-file="LoginPage.tsx">
                  <Globe className="h-6 w-6" data-sentry-element="Globe" data-sentry-source-file="LoginPage.tsx" />
                  <span className="sr-only">{t('login.select_language')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-sentry-element="DropdownMenuContent" data-sentry-source-file="LoginPage.tsx">
                {locales.map(loc => <DropdownMenuItem key={loc.code} onClick={() => {
                setLocale(loc.code);
              }}>
                    <span className={`fi fi-${loc.code} mr-2`}></span>{' '}
                    {loc.name}
                    {locale === loc.code && <Check className="h-4 w-4 ml-2" />}
                  </DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="space-y-6">
            <div className="flex flex-col items-center">
              <StudioshotLogo className="h-8 w-auto text-primary" data-sentry-element="StudioshotLogo" data-sentry-source-file="LoginPage.tsx" />
            </div>
            <SignIn appearance={{
            elements: {
              header: 'hidden',
              footer: 'hidden',
              formButtonPrimary: 'bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-6 rounded-full text-sm tracking-tight',
              socialButtonsBlockButton: 'bg-white text-primary font-semibold py-3 px-6 rounded-full text-sm tracking-tight',
              footerAction: {
                display: 'none'
              },
              footerSecondary: {
                display: 'none'
              }
            }
          }} withSignUp routing="hash" fallbackRedirectUrl={redirectUrl} signUpFallbackRedirectUrl={signUpRedirectUrl} signInUrl="/login" signUpUrl="/signup" data-sentry-element="SignIn" data-sentry-source-file="LoginPage.tsx" />
          </div>
        </div>
      </div>
    </div>;
}